import React, { PropsWithChildren } from 'react';
import { Currency, Col, Row } from '@common/components';
import {
  WEEK_DAY_LABELS,
  CORE_DAY_INPUT_KEYS,
  FLOAT_DAY_INPUT_KEYS,
  CORE_NIGHT_INPUT_KEYS,
  FLOAT_NIGHT_INPUT_KEYS,
  DYNAMIC_RATES_INPUT_KEYS,
} from '@scheduling/services';
import { UnitRateType, StaticPricingTier } from '@scheduling/graphql';

const [priceRange, incentiveIncrement] =
  DYNAMIC_RATES_INPUT_KEYS as (keyof UnitRateType)[];

const RateTableCell = ({
  rate,
  span,
  ...props
}: {
  rate: number;
  span: number;
}) => (
  <Col span={span} {...props}>
    <Currency value={rate || 0} />
  </Col>
);

const Label = ({ label }: { label: string }) => (
  <label className="color-midnight-lighter text-capitalize">{label}</label>
);

const TableRow = ({
  label,
  children,
}: PropsWithChildren<{ label: string }>) => (
  <Row>
    <Col span={4}>
      <Label label={label} />
    </Col>
    {children}
  </Row>
);

function RatesBasedOnKeys({
  keys,
  rates,
}: {
  keys: string[];
  rates: UnitRateType;
}) {
  return keys.map((key, index) => (
    <RateTableCell
      key={key}
      rate={rates[key as keyof UnitRateType] as number}
      span={index === keys.length - 1 ? 2 : 3}
    />
  ));
}

const Dynamic = ({ rates }: { rates: UnitRateType }) => (
  <Row gutter={[0, 8]}>
    <Col span={24}>
      <Row>
        <Col span={4}>
          <IncentiveRatesView.Label label="Price Range" />
        </Col>
        <Col span={4}>
          <Currency value={(rates[priceRange] as number) || 0} />
        </Col>
      </Row>
    </Col>
    <Col span={24}>
      <Row>
        <Col span={4}>
          <IncentiveRatesView.Label label="Price Increment" />
        </Col>
        <Col span={4}>
          <Currency value={(rates[incentiveIncrement] as number) || 0} />
        </Col>
      </Row>
    </Col>
  </Row>
);

const Variable = ({ rates }: { rates: UnitRateType }) => {
  const TableHeader = () => (
    <Row>
      <Col span={4} />
      {WEEK_DAY_LABELS.map((label, index) => (
        <Col key={label} span={index === WEEK_DAY_LABELS.length - 1 ? 2 : 3}>
          <Label label={label} />
        </Col>
      ))}
    </Row>
  );

  return (
    <Row gutter={[0, 16]}>
      <Col span={24}>
        <TableHeader />
      </Col>
      <Col span={24}>
        <TableRow label="Core Day">
          <RatesBasedOnKeys keys={CORE_DAY_INPUT_KEYS} rates={rates} />
        </TableRow>
      </Col>
      <Col span={24}>
        <TableRow label="Float Day">
          <RatesBasedOnKeys keys={FLOAT_DAY_INPUT_KEYS} rates={rates} />
        </TableRow>
      </Col>
      <Col span={24}>
        <TableRow label="Core Night">
          <RatesBasedOnKeys keys={CORE_NIGHT_INPUT_KEYS} rates={rates} />
        </TableRow>
      </Col>
      <Col span={24}>
        <TableRow label="Float Night">
          <RatesBasedOnKeys keys={FLOAT_NIGHT_INPUT_KEYS} rates={rates} />
        </TableRow>
      </Col>
    </Row>
  );
};

type StaticTierInput = Omit<StaticPricingTier, 'id'> & { id?: string };

const Static = ({
  tiers,
  displayAsLumpSum,
}: {
  tiers: StaticTierInput[];
  displayAsLumpSum?: boolean;
}) => {
  const columnSpans = {
    tierLabel: 8,
    fillRateRange: 8,
    payRate: 8,
  } as const;

  const sorted = [...tiers].sort(
    (a, b) => a.fillRateThreshold - b.fillRateThreshold
  );

  const fillRateRange = (index: number) => {
    const lower = index > 0 ? sorted[index - 1].fillRateThreshold : 0;
    const upper = sorted[index].fillRateThreshold;
    return `${lower}% - ${upper}%`;
  };

  return (
    <Row gutter={[0, 8]}>
      <Col span={24}>
        <Row>
          <Col span={columnSpans.tierLabel}>
            <Label label="Tier Label" />
          </Col>
          <Col span={columnSpans.fillRateRange}>
            <Label label="Fill Rate Range" />
          </Col>
          <Col span={columnSpans.payRate}>
            <Label label={displayAsLumpSum ? 'Total Shift Pay' : 'Pay Rate'} />
          </Col>
        </Row>
      </Col>
      {sorted.map((tier, index) => (
        <Col span={24} key={tier.id ?? index}>
          <Row>
            <Col span={columnSpans.tierLabel}>{tier.label}</Col>
            <Col span={columnSpans.fillRateRange}>{fillRateRange(index)}</Col>
            <Col span={columnSpans.payRate}>
              <Currency value={tier.payRate} />
              {!displayAsLumpSum && '/hr'}
            </Col>
          </Row>
        </Col>
      ))}
    </Row>
  );
};

export const IncentiveRatesView = {
  Variable,
  Dynamic,
  Static,
  Label,
};
